import { z } from 'zod';
import { MoneySchema } from './common.js';
// Wire schemas for the Consolidation module — spec §35 (TFRS 10).
// Group-scoped (not company-scoped): all endpoints sit under
// /consolidation/groups[/:id]/... with no :companyId in the path.
// Decimal-like: accept either a non-negative MoneySchema string or any number
// (Prisma serializes Decimal as string; the frontend often passes plain numbers).
const DecimalLike = z.union([MoneySchema, z.coerce.number()]);
// Signed numeric (BS rows / IS rows can be negative).
const SignedNumeric = z.union([z.string(), z.coerce.number()]);
const PeriodSchema = z.string().regex(/^\d{6}$/);
// =============================================================================
// Enums — mirror Prisma exactly.
// =============================================================================
export const ConsolidationGroupStatusSchema = z.enum(['ACTIVE', 'ARCHIVED']);
export const ConsolidationMethodSchema = z.enum(['FULL', 'EQUITY']);
export const ConsolidationEntryTypeSchema = z.enum([
    'ELIMINATION',
    'NCI_ADJUSTMENT',
    'FX_TRANSLATION',
    'OTHER',
]);
export const ConsolidationEntryStatusSchema = z.enum(['DRAFT', 'POSTED']);
// =============================================================================
// Group + Member entities
// =============================================================================
export const ConsolidationMemberSchema = z.object({
    id: z.uuid(),
    groupId: z.uuid(),
    companyId: z.uuid(),
    // 0 < pct <= 100. DCC group: DCC=100, TTOP=80, RCI=60.
    ownershipPercent: DecimalLike,
    consolidationMethod: ConsolidationMethodSchema,
    isParent: z.boolean(),
    // Convenience for the UI — populated by the service on read.
    companyCode: z.string().optional(),
    companyNameTh: z.string().optional(),
    companyColor: z.string().optional(),
});
export const ConsolidationGroupSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    parentCompanyId: z.uuid(),
    status: ConsolidationGroupStatusSchema,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    members: z.array(ConsolidationMemberSchema).optional(),
});
// =============================================================================
// Create / update DTOs
// =============================================================================
export const CreateConsolidationMemberSchema = z.object({
    companyId: z.uuid(),
    ownershipPercent: z.number().positive().max(100),
    consolidationMethod: ConsolidationMethodSchema.default('FULL'),
    isParent: z.boolean().default(false),
});
export const CreateConsolidationGroupSchema = z.object({
    name: z.string().min(1),
    parentCompanyId: z.uuid(),
    status: ConsolidationGroupStatusSchema.default('ACTIVE'),
    members: z.array(CreateConsolidationMemberSchema).min(1),
});
// =============================================================================
// ConsolidationEntry (elimination, NCI adjustment, etc.)
// =============================================================================
// Each line is an accountCode + Dr/Cr pair. Plain strings — Decimal.js parses
// them on the service side.
export const ConsolidationEntryLineSchema = z.object({
    accountCode: z.string().min(1),
    debit: DecimalLike,
    credit: DecimalLike,
    description: z.string().optional(),
});
export const ConsolidationEntrySchema = z.object({
    id: z.uuid(),
    groupId: z.uuid(),
    period: PeriodSchema,
    entryType: ConsolidationEntryTypeSchema,
    description: z.string(),
    lines: z.array(ConsolidationEntryLineSchema),
    totalDebit: DecimalLike,
    totalCredit: DecimalLike,
    status: ConsolidationEntryStatusSchema,
    postedBy: z.string().nullable(),
    postedAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const CreateConsolidationEntrySchema = z.object({
    period: PeriodSchema,
    entryType: ConsolidationEntryTypeSchema,
    description: z.string().min(1),
    lines: z.array(ConsolidationEntryLineSchema).min(1),
});
export const UpdateConsolidationEntrySchema = z.object({
    description: z.string().optional(),
    lines: z.array(ConsolidationEntryLineSchema).optional(),
    entryType: ConsolidationEntryTypeSchema.optional(),
});
// =============================================================================
// Read-side response shapes
// =============================================================================
// /financials — aggregate trial balance per member + combined column.
// Each row holds raw member balances and the combined total. The frontend
// applies eliminations to produce the consolidated column.
export const ConsolidationFinancialsRowSchema = z.object({
    accountCode: z.string(),
    accountName: z.string(),
    accountType: z.string(), // ASSET | LIABILITY | EQUITY | REVENUE | EXPENSE
    // per-member -> signed balance. Keyed by Company.code (e.g. 'DCC', 'TTOP').
    byMember: z.record(z.string(), SignedNumeric),
    combined: SignedNumeric,
});
export const ConsolidationFinancialsResponseSchema = z.object({
    groupId: z.uuid(),
    groupName: z.string(),
    period: PeriodSchema,
    members: z.array(z.object({
        companyId: z.uuid(),
        companyCode: z.string(),
        companyNameTh: z.string(),
        ownershipPercent: DecimalLike,
        consolidationMethod: ConsolidationMethodSchema,
        isParent: z.boolean(),
    })),
    rows: z.array(ConsolidationFinancialsRowSchema),
});
// /balance-sheet and /income-statement: similar shape — section header / line
// / sub / total rows with combined + elimination + consolidated columns.
export const ConsolidatedStatementRowSchema = z.object({
    kind: z.enum(['HEADER', 'SUB', 'LINE', 'TOTAL']),
    code: z.string().nullable(),
    label: z.string(),
    // per-member contribution (raw signed balance). Empty for HEADER rows.
    byMember: z.record(z.string(), SignedNumeric).optional(),
    combined: SignedNumeric,
    elimination: SignedNumeric,
    consolidated: SignedNumeric,
    indent: z.number().int().min(0).max(3).optional(),
});
export const ConsolidatedBalanceSheetResponseSchema = z.object({
    groupId: z.uuid(),
    groupName: z.string(),
    asOf: z.coerce.date(),
    rows: z.array(ConsolidatedStatementRowSchema),
});
export const ConsolidatedIncomeStatementResponseSchema = z.object({
    groupId: z.uuid(),
    groupName: z.string(),
    dateFrom: z.coerce.date(),
    dateTo: z.coerce.date(),
    rows: z.array(ConsolidatedStatementRowSchema),
});
// /nci — per-subsidiary minority-interest breakdown.
export const NciBreakdownRowSchema = z.object({
    companyId: z.uuid(),
    companyCode: z.string(),
    companyNameTh: z.string(),
    ownershipPercent: DecimalLike,
    nciPercent: DecimalLike,
    // Subsidiary's standalone equity (capital + retained) at the asOf date.
    subsidiaryEquity: SignedNumeric,
    // NCI share = subsidiaryEquity * nciPercent / 100.
    nciAmount: SignedNumeric,
});
export const NciResponseSchema = z.object({
    groupId: z.uuid(),
    groupName: z.string(),
    asOf: z.coerce.date(),
    rows: z.array(NciBreakdownRowSchema),
    totalNci: SignedNumeric,
});
//# sourceMappingURL=consolidation.js.map